import type { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { formatPhoneNumber } from "@repo/lib";
import type { ReactNode } from "react";
import {
  FormControl,
  FormField,
  FormHandlerSubmit,
  FormItem,
  FormRoot,
  FormSubmitButton,
} from "@/components/form";
import { InputPhone } from "@/components/input-phone";
import { phoneSchema } from "@/lib/form-schemas/phone-schema";

export type UpsertPhoneFormValues = z.infer<typeof phoneSchema>;

export interface PublicPhoneNumberFormComponentProps {
  defaultPhone?: string;
  onSubmit: (values: UpsertPhoneFormValues) => Promise<void>;
  loading?: boolean;
  submitLabel?: string;
  children?: ReactNode;
}

export const PublicPhoneNumberFormComponent = ({
  defaultPhone = "",
  onSubmit,
  loading = false,
  submitLabel = "Confirmar número",
  children,
}: PublicPhoneNumberFormComponentProps): JSX.Element => {
  const form = useForm<UpsertPhoneFormValues>({
    resolver: zodResolver(phoneSchema),
    defaultValues: {
      phone: formatPhoneNumber(defaultPhone.replace("+55", "")),
    },
  });

  const phoneValue = form.watch("phone");
  const isSubmitDisabled = !phoneValue || phoneValue.trim() === "";

  return (
    <>
      {children}
      <div className="pt-4">
        <FormRoot {...form}>
          <FormHandlerSubmit handleSubmit={onSubmit}>
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <InputPhone
                      required
                      title="Telefone"
                      placeholder="(00) 00000-0000"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormSubmitButton
              disabled={isSubmitDisabled}
              type="button"
              onClick={() => {
                void form.handleSubmit(onSubmit)();
              }}
              loading={loading}
            >
              {submitLabel}
            </FormSubmitButton>
          </FormHandlerSubmit>
        </FormRoot>
      </div>
    </>
  );
};

export const PublicPhoneNumberFormTitle = ({
  children,
}: { children: ReactNode }): JSX.Element => (
  <h2 className="text-2xl font-semibold text-left">{children}</h2>
);

export const PublicPhoneNumberFormDescription = ({
  children,
}: { children: ReactNode }): JSX.Element => (
  <p className="text-sm text-neutral-400">{children}</p>
);
